import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { generarTicketBytes } from '../utils/escpos'

// Printer: service 0xFF00, write characteristic 0xFF02 (WRITE NO RESPONSE)
const SERVICE_UUID = '0000ff00-0000-1000-8000-00805f9b34fb'
const WRITE_UUID   = '0000ff02-0000-1000-8000-00805f9b34fb'

// Conservative chunk size for BLE WRITE NO RESPONSE (pre-MTU-negotiation safe value)
const CHUNK = 100
const CHUNK_DELAY = 30  // ms between chunks

const BTPrinterCtx = createContext(null)

export function BTPrinterProvider({ children }) {
  const [estado, setEstado] = useState('desconectado') // desconectado | conectando | listo | error
  const [nombre, setNombre] = useState(null)
  const charRef   = useRef(null)
  const deviceRef = useRef(null)

  useEffect(() => {
    // Silent auto-reconnect to previously granted printer on load
    if (!navigator.bluetooth?.getDevices) return
    navigator.bluetooth.getDevices()
      .then(devices => { if (devices.length) _conectar(devices[0]).catch(() => {}) })
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function _onDisconnect() {
    charRef.current = null
    setEstado('desconectado')
    // Retry connection after a short delay
    if (deviceRef.current) {
      setTimeout(() => _conectar(deviceRef.current).catch(() => {}), 4000)
    }
  }

  async function _conectar(device) {
    setEstado('conectando')
    try {
      device.removeEventListener('gattserverdisconnected', _onDisconnect)
      device.addEventListener('gattserverdisconnected', _onDisconnect)
      const server = await device.gatt.connect()
      const service = await server.getPrimaryService(SERVICE_UUID)
      const char = await service.getCharacteristic(WRITE_UUID)
      charRef.current = char
      deviceRef.current = device
      setNombre(device.name || 'Impresora BT')
      setEstado('listo')
    } catch {
      charRef.current = null
      setEstado('desconectado')
      throw new Error('No se pudo conectar a la impresora')
    }
  }

  const parear = useCallback(async () => {
    if (!navigator.bluetooth) throw new Error('Web Bluetooth no disponible en este navegador')
    const device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: [SERVICE_UUID],
    })
    await _conectar(device)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const desconectar = useCallback(() => {
    try { deviceRef.current?.gatt?.disconnect() } catch {}
    deviceRef.current = null
    charRef.current = null
    setEstado('desconectado')
    setNombre(null)
  }, [])

  const imprimir = useCallback(async (venta, config, folio) => {
    if (!charRef.current) throw new Error('Impresora no conectada')
    const bytes = generarTicketBytes(venta, config, folio)
    for (let i = 0; i < bytes.length; i += CHUNK) {
      await charRef.current.writeValueWithoutResponse(bytes.slice(i, i + CHUNK))
      await new Promise(r => setTimeout(r, CHUNK_DELAY))
    }
  }, [])

  return (
    <BTPrinterCtx.Provider value={{ estado, nombre, parear, desconectar, imprimir }}>
      {children}
    </BTPrinterCtx.Provider>
  )
}

export const useBTPrinter = () => useContext(BTPrinterCtx)
